import { writable } from 'svelte/store';
import { appSettings, getMeta } from '../store.js';
import { teams as ffplTeams } from '../teams-data.js';

// Global Svelte state replacing router's local state
export const currentScreen = writable('home');
export const currentScreenProps = writable(null); // Props for the current screen

// User Authentication State
export const currentUser = writable(null);
export const authInitialized = writable(false);
export const showLoginModal = writable(false);
export const showPremiumModal = writable(false);

// Soft "new version available" nudge (non-blocking; set from main.js).
export const updateAvailable = writable(false);

// Guest mode — lets people score local matches without a Google account.
// Cloud sync / profiles / recorder features stay behind login.
function _initialGuestMode() {
  try { return localStorage.getItem('cs_guest_mode') === '1'; } catch (_) { return false; }
}
export const guestMode = writable(_initialGuestMode());
guestMode.subscribe(v => {
  try {
    if (v) localStorage.setItem('cs_guest_mode', '1');
    else localStorage.removeItem('cs_guest_mode');
  } catch (_) {}
});

// Authoritative reactive application state
export const activeMatch = writable(null);
export const savedMatches = writable([]);
export const matchSetup = writable({
  teamA: null, teamB: null,
  overs: 10, mode: "full", lms: true,
  tossWinner: "A", tossDecision: "bat",
  rules: {
    maxOversPerBowler: 2,
    wideValue: 1,
    noBallValue: 1,
    rebowlWides: true,
    rebowlNoBalls: true
  },
  challenge: {
    type: 'target_runs', // target_runs, target_sixes, target_wickets, super_over
    targetValue: 15
  }
});

// App Settings as a reactive store
export const settings = writable(appSettings || {});

// Recording & Camera State
export const isRecording = writable(false);
export const isPaused = writable(false);
export const recElapsed = writable("00:00");
export const cameraOk = writable(false);
export const cameraErr = writable(null);
export const modalState = writable(null);
export const fullScreenBoard = writable(null);
export const fullScreenBoardStartedAt = writable(Date.now());
export const lastBallState = writable(null);
export const activeLowerThird = writable(null);
export const autoSplitInProgress = writable(false);
export const inningsEndPending = writable(false);
export const actualDrawFps = writable(null);
export const recordingPerformanceWarning = writable(false);
export const cameraInterruption = writable({
  status: 'live',
  interruptedAt: null,
  totalInterruptedMs: 0,
  recoveryAttempts: 0,
  message: ''
});
export const renderProcessRecovery = writable({
  supported: false,
  pending: false,
  reason: null,
  occurredAt: null,
  crashCount: 0,
  loopGuarded: false,
  activeRecordingId: null
});
export const deviceCheckReport = writable(null);
export const deviceCheckProgress = writable({ running: false, activeCheck: null });
export const deviceCheckOpen = writable(false);
export const recordingHealth = writable({ status: 'idle', signals: {}, metrics: {} });
export const recordingSafetyOpen = writable(false);
export const recoverableRecordings = writable([]);
export const pendingGallerySessions = writable([]);
export const directGalleryStatus = writable({
  capability: 'unknown',
  active: false,
  writerKind: null,
  bytesAcknowledged: 0,
  chunksAcknowledged: 0,
  queuedBytes: 0,
  lastAckAt: null,
  state: 'idle',
  message: ''
});
export const recordingSaveState = writable({
  status: 'idle',
  message: '',
  fileName: '',
  bytes: 0,
  recordingId: null
});
export const activeAnimation = writable(null);
export const animationQueue = writable([]);
export const recoveryInfo = writable({ available: false, restored: false, warning: "", latest: null, previous: null });

// Network & Peer Sync State
export const peerRole = writable(null); // "scorer" | "recorder" | null
export const peerConnected = writable(false);
export const peerRoomCode = writable(null);
export const peerStatus = writable('disconnected'); // disconnected, connecting, connected, error, failed, reconnecting

// Available teams (combining FFPL with custom teams and meta overrides)
function getInitialTeams() {
  let custom = [];
  try {
    custom = JSON.parse(localStorage.getItem('cs_custom_teams_v1') || "[]");
  } catch (_) {}
  
  const ffpl = ffplTeams.map(t => {
    const override = getMeta(t.name);
    return { ...t, ...override };
  });

  const merged = [...custom];
  const customNames = new Set(custom.map(t => t.name));
  
  ffpl.forEach(t => {
    if (!customNames.has(t.name)) {
      merged.push(t);
    }
  });
  
  return merged;
}

export const availableTeams = writable(getInitialTeams());
